import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { useEffect } from 'react';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { CategoryProvider } from '@/contexts/CategoryContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { Header } from '@/app/components/Header';
import { ProjectsPage } from '@/app/pages/ProjectsPage';
import { AboutPage } from '@/app/pages/AboutPage';
import { ContactPage } from '@/app/pages/ContactPage';
import { ProjectDetailPage } from '@/app/pages/ProjectDetailPage';
import { NewsPage } from '@/app/pages/NewsPage';
import { NewsDetailPage } from '@/app/pages/NewsDetailPage';
import { LoginPage } from '@/app/pages/LoginPage';
import { TestPage } from '@/app/pages/TestPage';
import { AdminDashboard } from '@/app/pages/AdminDashboard';
import { checkBackendHealth } from '@/services/api';

// Suppress ReactQuill findDOMNode warnings (library issue, not our code)
const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('findDOMNode') || 
     args[0].includes('Warning: findDOMNode is deprecated'))
  ) {
    return;
  }
  originalError.apply(console, args);
};

console.warn = (...args) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('findDOMNode') || 
     args[0].includes('Warning: findDOMNode is deprecated') ||
     args[0].includes('A component is changing a controlled input'))
  ) {
    return;
  }
  originalWarn.apply(console, args);
};

// Run health check on app start
(async () => {
  console.log('🚀 Application starting...');
  const health = await checkBackendHealth();
  if (!health.ok) {
    console.warn('⚠️ Backend health check failed:', health.error);
    console.warn('⚠️ This is normal in Figma Make preview - backend will work in production');
    console.warn('⚠️ The app will use local data when available');
  } else {
    console.log('✅ Backend is healthy and responding');
  }
})();

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Component to handle scroll restoration
function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    // Restore scroll position for home/projects page when navigating back
    if (location.pathname === '/' || location.pathname === '/projects') {
      const savedScroll = sessionStorage.getItem('projectsScrollY');
      if (savedScroll) {
        const scrollY = parseInt(savedScroll, 10);
        console.log('ScrollManager - Restoring scroll to:', scrollY);
        
        // Function to check if page is ready and restore scroll
        const restoreScroll = () => {
          const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
          
          if (maxScrollY >= scrollY) {
            // Page has enough content, restore scroll
            window.scrollTo(0, scrollY);
            console.log('ScrollManager - Scroll restored, current position:', window.scrollY);
            // Clear the saved scroll after restoring
            sessionStorage.removeItem('projectsScrollY');
          } else {
            // Page doesn't have enough content yet, wait a bit
            console.log('ScrollManager - Waiting for content to load... maxScrollY:', maxScrollY);
            setTimeout(restoreScroll, 50);
          }
        };
        
        // Start attempting to restore scroll
        requestAnimationFrame(() => {
          setTimeout(restoreScroll, 100);
        });
      } else {
        // No saved scroll position, go to top
        window.scrollTo(0, 0);
      }
    } else {
      // For other pages, scroll to top
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return null;
}

// Layout with Header
function LayoutWithHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <Header />
      {children}
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <SettingsProvider>
        <AuthProvider>
          <CategoryProvider>
            <BrowserRouter>
              <ScrollManager />
              <Routes>
                {/* Public routes with header */}
                <Route
                  path="/"
                  element={
                    <LayoutWithHeader>
                      <ProjectsPage />
                    </LayoutWithHeader>
                  }
                />
                <Route
                  path="/project/:id"
                  element={
                    <LayoutWithHeader>
                      <ProjectDetailPage />
                    </LayoutWithHeader>
                  }
                />
                <Route
                  path="/about"
                  element={
                    <LayoutWithHeader>
                      <AboutPage />
                    </LayoutWithHeader>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <LayoutWithHeader>
                      <ContactPage />
                    </LayoutWithHeader>
                  }
                />
                <Route
                  path="/news"
                  element={
                    <LayoutWithHeader>
                      <NewsPage />
                    </LayoutWithHeader>
                  }
                />
                <Route
                  path="/news/:id"
                  element={
                    <LayoutWithHeader>
                      <NewsDetailPage />
                    </LayoutWithHeader>
                  }
                />
                
                {/* Login route */}
                <Route path="/login" element={<LoginPage />} />
                
                {/* Protected admin routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </BrowserRouter>
          </CategoryProvider>
        </AuthProvider>
      </SettingsProvider>
    </LanguageProvider>
  );
}